/**
 * Rule Book citations, taken out of text a person reads.
 *
 * Here rather than in `portal-kit`, for the reason `locales.ts` gives about the
 * shipped language list: the consoles are not the only readers. The server
 * keeps citing the Rule Book in what it writes — and matches on those
 * citations in a database's refusals, so they stay in the source — but a
 * statement PDF and a report downloaded as CSV are read without a console in
 * between, and they need the same stripping one import away.
 */
/**
 * One citation — "§34.10a", "§36.2b(ii)", "§29.4 step 6" — and a run of them
 * joined by "/" or ",", which is how the server cites two rules at once. A
 * decision number ("E28") rides in a run only behind a section: on its own,
 * "E28" is as likely to be something else.
 */
const REF = String.raw `§[\d.a-zA-Z]+(?:\([ivx]+\))?(?:\s+(?:step|layer)\s+\d+)?`;
const REFS = String.raw `${REF}(?:\s*[/,]\s*(?:${REF}|E\d+\b))*`;
/**
 * WEB-R4 — the longest text the full set of patterns below is run on.
 *
 * Several of them start with an unbounded `\s*` or repeat a citation chain
 * ahead of a lookahead, which is quadratic on a crafted string: a sub-ID of
 * `§` and 50 000 spaces took 20 seconds of the API's one thread when an
 * operator downloaded a CSV. The server's own cited sentences are a line or
 * two, so anything longer is somebody's text, and gets the single-pass
 * version: every citation still goes, only the punctuation around it is not
 * tidied as carefully.
 */
export const MAX_FULLY_STRIPPED_LENGTH = 1000;
export function stripRuleReferences(text) {
    if (text.length > MAX_FULLY_STRIPPED_LENGTH)
        return stripRuleReferencesLinear(text);
    let s = shortenWhitespaceRuns(text).replace(/Rule Book\s+/gi, '');
    // "(§24.2, optional)" keeps its flag and "(§14.10d — the click's own geo)"
    // keeps what it says; a parenthesis that only cited goes.
    s = s.replace(new RegExp(String.raw `\(\s*${REFS}(?:\s*,\s*(optional|required))?(?:(?:\s*[—–:-]\s*|\s+)([^)]*))?\s*\)`, 'gi'), (_, flag, said) => flag ? `(${flag.toLowerCase()})` : said?.trim() ? `(${said.trim()})` : '');
    // A citation opening a sentence — "§34.10a: review the cluster…" — goes with
    // its colon or dash, and the sentence starts again with a capital.
    s = s.replace(new RegExp(String.raw `(^|[.!?]\s+)${REFS}\s*[:—–-]\s*(\p{Ll})?`, 'gmu'), (_, before, first) => before + (first ? first.toUpperCase() : ''));
    // `(?<!\s)` makes each of the next three start only where a whitespace run
    // starts, not at every character inside one (WEB-R4). The leftmost match is
    // the same: a greedy `\s*` from the start of a run takes the whole run.
    s = s.replace(new RegExp(String.raw `(?<!\s)\s*[—–-]\s*${REFS}`, 'g'), '');
    // "(operator-wide, §22.6a, §21.2)" — the citations close a parenthesis that
    // says something else first.
    s = s.replace(new RegExp(String.raw `(?<!\s)\s*,\s*${REFS}(?=\s*\))`, 'g'), '');
    s = s.replace(new RegExp(String.raw `\(?${REFS}\)?`, 'g'), '');
    s = s.replace(/(?<!\s)\s*\/\s*E\d+/g, '');
    return tidy(s);
}
/**
 * Every whitespace run of two or more, as exactly two characters (WEB-R4).
 *
 * The parenthesis pattern below has five `\s*` and `\s+` in a row, and
 * a run of n spaces can be split between them in about n^4 ways: "(§1"
 * and 2 000 spaces took six seconds. Two characters cannot be split many ways.
 * Nothing the patterns decide depends on the length of a run beyond "two or
 * more", which `tidy` collapses to one space at the end either way, nor on
 * anything but its last character, which is kept when it ends a line so a
 * citation opening the next line is still seen as opening it.
 */
function shortenWhitespaceRuns(text) {
    return text.replace(/\s{2,}/g, (run) => {
        const last = run[run.length - 1];
        return /[\n\r\u2028\u2029]/.test(last) ? ` ${last}` : '  ';
    });
}
/**
 * The long-text form: each pattern starts only at a `§`, a `(` or the letter
 * R, and consumes what it matched, so the whole pass is linear.
 */
function stripRuleReferencesLinear(text) {
    const s = text
        .replace(/Rule Book\s+/gi, '')
        .replace(new RegExp(String.raw `\(?${REF}\)?`, 'g'), '');
    return tidy(s);
}
/**
 * What is left once the citations are out. Whitespace runs are collapsed
 * first, so every pattern after that sees runs of one character at most.
 */
function tidy(s) {
    return s
        .replace(/§/g, '')
        .replace(/\s{2,}/g, ' ')
        .replace(/\s+([,.;:])/g, '$1')
        .replace(/\(\s*\)/g, '')
        .replace(/\s+,/g, ',')
        .replace(/([.!?]):/g, '$1')
        .trim();
}
/**
 * Every string in an API answer, with its citations removed.
 *
 * The server goes on citing the Rule Book in what it says — an error, a fraud
 * case's explanation, a hold reason — and many of those sentences are stored
 * and read back long after they were written. Stripping them one render at a
 * time left every screen that prints a field directly showing "§34.10a" to the
 * operator. Done once, where a console parses the answer (and where a report
 * becomes a CSV), no screen can forget.
 *
 * Only a string carrying a `§` is touched: `stripRuleReferences` also collapses
 * whitespace and trims, which is right for a cited sentence and wrong for a
 * value somebody typed.
 */
export function stripRuleReferencesDeep(value) {
    if (typeof value === 'string')
        return value.includes('§') ? stripRuleReferences(value) : value;
    if (Array.isArray(value))
        return value.map(stripRuleReferencesDeep);
    if (value !== null && typeof value === 'object') {
        const out = {};
        for (const [key, item] of Object.entries(value))
            out[key] = stripRuleReferencesDeep(item);
        return out;
    }
    return value;
}
//# sourceMappingURL=rule-references.js.map